import React, { useState, useEffect } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { gradingAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import GradeBoundaryFormDialog from './forms/GradeBoundaryFormDialog';
import GradingScaleFormDialog from './forms/GradingScaleFormDialog';
import BulkGradeBoundaryDialog from './forms/BulkGradeBoundaryDialog';

const INITIAL_BOUNDARY = { grading_scale_id: '', grade: '', min_percentage: '', max_percentage: '', grade_point: '', aggregate_value: '', description: '' };
const INITIAL_SCALE = { scale_name: '', scale_code: '', description: '', is_default: false, is_active: true };

const extract = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const GradeBoundaries = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.DELETE);
  const [boundaries, setBoundaries] = useState([]);
  const [scales, setScales] = useState([]);
  const [selectedScale, setSelectedScale] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [savingScale, setSavingScale] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [showScaleForm, setShowScaleForm] = useState(false);
  const [editingBoundary, setEditingBoundary] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(INITIAL_BOUNDARY);
  const [scaleFormData, setScaleFormData] = useState(INITIAL_SCALE);

  useEffect(() => { fetchAll(); }, [canRead]);

  const fetchAll = async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const [bRes, sRes] = await Promise.all([
        gradingAPI.getGradeBoundaries({ per_page: 1000 }),
        gradingAPI.getActiveGradingScales(),
      ]);
      setBoundaries(extract(bRes));
      setScales(extract(sRes));
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const fetchBoundaries = async () => {
    try {
      const res = await gradingAPI.getGradeBoundaries({ per_page: 1000 });
      setBoundaries(extract(res));
    } catch { /* silent */ }
  };

  const filtered = selectedScale ? boundaries.filter((b) => Number(b.grading_scale_id) === Number(selectedScale)) : boundaries;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const scaleId = selectedScale || formData.grading_scale_id;
      const payload = { ...formData, grading_scale_id: scaleId };
      if (editingBoundary) {
        await gradingAPI.updateGradeBoundary(editingBoundary.id, payload);
        setSnackbar({ open: true, message: 'Grade boundary updated successfully', severity: 'success' });
      } else {
        await gradingAPI.createGradeBoundary(payload);
        setSnackbar({ open: true, message: 'Grade boundary created successfully', severity: 'success' });
      }
      fetchBoundaries();
      resetForm();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleEdit = (b) => { setEditingBoundary(b); setFormData(b); setShowForm(true); };
  const resetForm = () => { setFormData(INITIAL_BOUNDARY); setEditingBoundary(null); setShowForm(false); };

  const confirmDeleteAction = async () => {
    try {
      await gradingAPI.deleteGradeBoundary(confirmDelete);
      setSnackbar({ open: true, message: 'Grade boundary deleted successfully', severity: 'success' });
      fetchBoundaries();
    } catch { setSnackbar({ open: true, message: 'Failed to delete grade boundary', severity: 'error' }); }
    finally { setConfirmDelete(null); }
  };

  // Uses the backend bulk endpoint: POST /boundaries with { boundaries: [...] }
  const handleBulkSubmit = async (rows, onSuccess) => {
    setSavingBulk(true);
    try {
      const scaleId = selectedScale || rows[0]?.grading_scale_id;
      const boundaries_payload = rows.map((r) => ({
        ...r,
        grading_scale_id: Number(scaleId),
        min_percentage: Number(r.min_percentage),
        max_percentage: Number(r.max_percentage),
        grade_point: r.grade_point !== '' ? Number(r.grade_point) : null,
        aggregate_value: r.aggregate_value !== '' ? Number(r.aggregate_value) : null,
      }));

      const invalid = boundaries_payload.find((r) => Number(r.max_percentage) < Number(r.min_percentage));
      if (invalid) {
        setSnackbar({ open: true, message: `Grade "${invalid.grade}": Max % (${invalid.max_percentage}) must be ≥ Min % (${invalid.min_percentage})`, severity: 'error' });
        setSavingBulk(false);
        return;
      }

      const sorted = [...boundaries_payload].sort((a, b) => a.min_percentage - b.min_percentage);
      for (let i = 0; i < sorted.length - 1; i++) {
        if (Number(sorted[i].max_percentage) >= Number(sorted[i + 1].min_percentage)) {
          setSnackbar({ open: true, message: `Grades "${sorted[i].grade}" (${sorted[i].min_percentage}-${sorted[i].max_percentage}%) and "${sorted[i+1].grade}" (${sorted[i+1].min_percentage}-${sorted[i+1].max_percentage}%) overlap`, severity: 'error' });
          setSavingBulk(false);
          return;
        }
      }

      await gradingAPI.createGradeBoundary({ boundaries: boundaries_payload });
      setSnackbar({ open: true, message: `${rows.length} boundaries added successfully`, severity: 'success' });
      fetchBoundaries();
      setShowBulkForm(false);
      onSuccess();
    } catch (error) {
      const msg = error?.response?.data?.message || Object.values(error?.response?.data?.errors || {}).flat().join(', ') || 'Bulk insert failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally { setSavingBulk(false); }
  };

  const handleScaleSubmit = async (e) => {
    e.preventDefault();
    setSavingScale(true);
    try {
      const response = await gradingAPI.createGradingScale(scaleFormData);
      const created = response?.data?.data || response?.data;
      setSnackbar({ open: true, message: 'Grading scale added successfully', severity: 'success' });
      const sRes = await gradingAPI.getActiveGradingScales();
      setScales(extract(sRes));
      if (created?.id) setSelectedScale(created.id);
      setScaleFormData(INITIAL_SCALE);
      setShowScaleForm(false);
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to add grading scale', severity: 'error' });
    } finally { setSavingScale(false); }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view grade boundaries.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Grade Boundaries"
        subtitle="Define percentage ranges for each grade"
        actions={[
          ...(canCreate ? [<Button key="add-scale" variant="outlined" onClick={() => setShowScaleForm(true)} fullWidth={isMobile}>Add Scale</Button>] : []),
          ...(canCreate ? [<Button key="bulk" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowBulkForm(true)} fullWidth={isMobile}>Bulk Add</Button>] : []),
          ...(canCreate ? [<Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)} fullWidth={isMobile}>Add Grade Boundary</Button>] : []),
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Autocomplete
              size="small"
              sx={{ width: isMobile ? '100%' : 320 }}
              options={scales}
              value={scales.find((s) => Number(s.id) === Number(selectedScale)) || null}
              onChange={(_, v) => setSelectedScale(v?.id || '')}
              getOptionLabel={(o) => `${o.scale_name || 'Scale'} (${o.scale_code || '-'})`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Filter by Scale" />}
            />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : filtered.length === 0 ? (
            <EmptyRecordsState message={selectedScale ? 'No boundaries found for the selected scale.' : 'No grade boundaries found.'} />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={filtered}
              renderItem={(b) => (
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={700}>{b.grade}</Typography>
                    <Typography variant="body2" color="text.secondary">{b.min_percentage}% - {b.max_percentage}%</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{b.grading_scale?.scale_name || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">Grade point: {b.grade_point || '-'} | Aggregate: {b.aggregate_value || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">{b.description || ''}</Typography>
                  <Box textAlign="right">
                    {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(b)}><EditIcon /></IconButton>}
                    {canDelete && <IconButton size="small" color="error" onClick={() => setConfirmDelete(b.id)}><DeleteIcon /></IconButton>}
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Grade</TableCell>
                    <TableCell>Scale</TableCell>
                    <TableCell>Min %</TableCell>
                    <TableCell>Max %</TableCell>
                    <TableCell>Grade Point</TableCell>
                    <TableCell>Aggregate</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell><strong>{b.grade}</strong></TableCell>
                      <TableCell>{b.grading_scale?.scale_name || '-'}</TableCell>
                      <TableCell>{b.min_percentage}%</TableCell>
                      <TableCell>{b.max_percentage}%</TableCell>
                      <TableCell>{b.grade_point ?? '-'}</TableCell>
                      <TableCell>{b.aggregate_value ?? '-'}</TableCell>
                      <TableCell>{b.description}</TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(b)}><EditIcon /></IconButton>}
                        {canDelete && <IconButton size="small" color="error" onClick={() => setConfirmDelete(b.id)}><DeleteIcon /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Grade Boundary</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this grade boundary?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <GradeBoundaryFormDialog open={showForm} onClose={resetForm} onSubmit={handleSubmit} editingBoundary={editingBoundary} formData={formData} setFormData={setFormData} saving={saving} isMobile={isMobile} />
      <BulkGradeBoundaryDialog open={showBulkForm} onClose={() => setShowBulkForm(false)} onSubmit={handleBulkSubmit} saving={savingBulk} scales={scales} selectedScale={selectedScale} isMobile={isMobile} />
      <GradingScaleFormDialog open={showScaleForm} onClose={() => setShowScaleForm(false)} onSubmit={handleScaleSubmit} editingScale={null} formData={scaleFormData} setFormData={setScaleFormData} saving={savingScale} isMobile={isMobile} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GradeBoundaries;
